<!--@format-->
<template>
    <div class="mdf-payment-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-show="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-show="!isLoading" class="widget-container">
                    <mdf-make-payment-step
                        v-if="currentStep === steps.makePaymentStep"
                        ref="contentStep"
                        :payment-data="paymentDetails"
                        :invoice-table-details="invoiceTableDetails"
                        :account-id="accountId"
                        @next-step="goToStep('successStep')"
                    />

                    <mdf-success-step
                        v-if="currentStep === steps.successStep"
                        ref="usersStep"
                        :invoice-table-details="invoiceTableDetails"
                        :payment-details="paymentDetails"
                        :invoice-id="widget.invoiceId"
                        @update:payment-details="updateDetails"
                        @next-step="closeWidget"
                        @view-invoice="handleViewInvoice"
                        @update:loading="updateIsLoading"
                    />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button
                        v-if="currentStep.firstButton"
                        class="cancel-btn"
                        @click="closeWidget"
                    >
                        Cancel
                    </app-button>
                </div>
                <app-button
                    :class="[
                        {
                            'save-btn': !currentStep.finalStep,
                            'save-final-btn': currentStep.finalStep,
                        },
                    ]"
                    :loading="isPaying"
                    :disabled="isLoading"
                    @click="currentStep.actionButton"
                >
                    {{ currentStep.secondButton }}
                </app-button>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const MdfSuccessStep = () =>
    import('@/components/sliding-widgets/mdf-payment-widget/mdf-success-step');
const MdfMakePaymentStep = () =>
    import('@/components/sliding-widgets/mdf-payment-widget/mdf-make-payment-step');

import EventBus from '@/scripts/event-bus.js';
import { PaymentMethodsService } from '@/scripts/services/payment-methods/payment-methods-service';
import { MDFInvoicesService } from '@/scripts/services/invoices/mdf-invoices-service.js';
import { FeeEnumMixin } from '@/mixins/fee-enum-mixin';

export default {
    name: 'mdf-payment-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'mdf-success-step': MdfSuccessStep,
        'mdf-make-payment-step': MdfMakePaymentStep,
    },

    mixins: [FeeEnumMixin],

    provide() {
        return {
            clientId: this.widget.clientId,
            agencyId: this.widget.agencyId,
            accountId: this.widget.accountId,
        };
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            agencyId: this.widget.agencyId,

            accountId: this.widget.accountId,

            currentStep: null,

            isLoading: false,

            isPaying: false,

            paymentDetails: {
                selectedPaymentOption: 'payInGlasshive',
                baseFee: '',
                items: [],
                processingFee: '',
                serviceFee: '',
                transactionDate: '',
                paymentMethods: [],
                payMethod: {},
                agreement: false,
                companyName: '',
                amount: '',
                invoiceId: '',
                status: '',
                clientHasBankAccount: false,
                clientAccountId: undefined,
                paymentOptions: [
                    {
                        value: 'payInGlasshive',
                        title: 'Pay in Glasshive',
                    },
                    {
                        value: 'markAsPaid',
                        title: 'Mark as paid',
                        description: 'You will send the payment outside Glasshive',
                    },
                ],
            },

            steps: {
                makePaymentStep: {
                    title: 'Make a Payment',
                    firstButton: 'Cancel',
                    secondButton: 'Pay Now',
                    finalStep: false,
                    loading: false,
                    actionButton: () => {
                        this.isPaying = true;
                        this.$refs.contentStep.next();
                    },
                },

                successStep: {
                    title: 'Success',
                    firstButton: null,
                    secondButton: 'Next',
                    finalStep: true,
                    loading: false,
                    actionButton: () => {
                        this.$refs.usersStep.next();
                    },
                },
            },
        };
    },

    computed: {
        invoiceTableDetails() {
            if (this.paymentDetails.selectedPaymentOption === 'payInGlasshive') {
                return Object.keys(this.invoiceDetails).map((key) => this.invoiceDetails[key]);
            }
            return [
                this.invoiceDetails.companySection,
                [this.invoiceDetails.feeSection[0]],
                this.invoiceDetails.amountSection,
            ];
        },

        invoiceDetails() {
            return this.getInvoiceDetails(this.paymentDetails);
        },
    },

    created() {
        EventBus.$on('payment:finished', this.handlePaymentFinished);
        this.goToStep('makePaymentStep');
        this.initInvoiceData();
    },

    beforeDestroy() {
        EventBus.$off('payment:finished', this.handlePaymentFinished);
    },

    methods: {
        initInvoiceData() {
            this.isLoading = true;
            this.getInvoiceData()
                .then(this.getCards)
                .then(this.getVerifyClientHasBankAccount)
                .finally(() => {
                    this.isLoading = false;
                });
        },
        
        getInvoiceData() {
            return new Promise((resolve, reject) => {
                MDFInvoicesService.getPaymentDetailsById(
                    this.widget.invoiceId,
                    this.widget.accountId,
                )
                    .then(({ data: { data } }) => {
                        this.paymentDetails = {
                            ...this.paymentDetails,
                            companyName: data.PayableTo,
                            amount: data.AmountDue,
                            invoiceId: data.InvoiceId,
                            clientAccountId: data.ClientAccountId,
                            status: data.Status,
                            baseFee: data.BaseFee,
                            items: data.Items,
                            processingFee: data.ProcessingFee,
                            serviceFee: data.ServiceFee,
                            transactionDate: data.TransactionDate,
                            isMarkedAsPaid:
                                data.Status === 'paid' && data.PaymentMethod === 'Marked as Paid',
                            isPaid: data.Status === 'paid',
                        };
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your information.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getVerifyClientHasBankAccount() {
            return new Promise((resolve, reject) => {
                MDFInvoicesService.getClientHasBankAccount({
                    invoiceId: this.paymentDetails.invoiceId,
                    clientAccountId: this.paymentDetails.clientAccountId,
                })
                    .then(({ data: { data } }) => {
                        this.paymentDetails.clientHasBankAccount = data;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your information.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        getInvoiceDetails(details) {
            return {
                companySection: [
                    {
                        title: 'Company Name',
                        content: details.companyName,
                    },
                    {
                        title: 'Invoice Number',
                        content: details.invoiceId,
                    },
                    {
                        title: details.items && details.items.length > 1 ? 'Items' : 'Item',
                        content: details.items
                            ? details.items.map((item) => item.FundTypeName).join(', ')
                            : '',
                    },
                    {
                        title: 'Transaction Date',
                        content: this.formatDate(details.transactionDate),
                        contentClass: 'uppercase',
                    },
                ],

                feeSection: [
                    {
                        title: 'Base Fee',
                        content: details.baseFee,
                        type: 'currency',
                    },
                    {
                        title: 'GlassHive Service Fee',
                        additionalTitleInfo: `(${this.feeEnum.mdfGlasshiveFee})`,
                        content: details.serviceFee,
                        type: 'currency',
                    },
                    {
                        title: 'Processing Fees',
                        additionalTitleInfo: `(${this.feeEnum.mdfProcessingFee})`,
                        content: details.processingFee,
                        type: 'currency',
                    },
                    ...(details.paymentMethod
                        ? [
                              {
                                  title: 'Payment Method',
                                  content: details.paymentMethod.paymentType,
                                  contentDetails: details.paymentMethod.cardDetails,
                                  contentClass: 'capitalize',
                              },
                          ]
                        : []),
                ],

                amountSection: [
                    {
                        title: this.currentStep === this.steps.successStep ? 'Total' : 'Amount Due',
                        content:
                            details.selectedPaymentOption === 'payInGlasshive'
                                ? details.amount
                                : details.baseFee,

                        rowClass: this.currentStep !== this.steps.successStep ? 'extra-bold' : '',
                        contentClass:
                            this.currentStep === this.steps.successStep ? 'total-amount' : '',

                        type: 'currency',
                    },
                ],
            };
        },

        getCards() {
            return new Promise((resolve, reject) => {
                PaymentMethodsService.getAllVerified(this.agencyId, 'agency')
                    .then(({ data }) => {
                        this.paymentDetails.paymentMethods = data.data
                            .map(this.mapItems)
                            .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
                        this.paymentDetails.payMethod = this.paymentDetails.paymentMethods[0];
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your information.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        convertToFourDigitYear(twoDigitYear) {
            if (!twoDigitYear || isNaN(twoDigitYear)) {
                return null;
            }
            const currentYear = new Date().getFullYear();
            const currentCentury = Math.floor(currentYear / 100) * 100;
            return currentCentury + parseInt(twoDigitYear, 10);
        },

        mapItems(item) {
            if (item.PaymentType === 'card') {
                const month =
                    item.CardExpirationMonth && !isNaN(item.CardExpirationMonth)
                        ? parseInt(item.CardExpirationMonth, 10) - 1
                        : null;
                const year = this.convertToFourDigitYear(item.CardExpirationYear);
                let expirationDate = '';
                if (month !== null && year !== null) {
                    expirationDate = this.$moment().year(year).month(month);
                }
                return {
                    type: item.Type,
                    cardType: item.CardType,
                    lastFourNumbers: item.CardLastFour,
                    isDefault: item.IsDefault,
                    key: `${item.CardLastFour}-${item.CardType}`,
                    id: item.Id,
                    imageCard: this.$helpers.setCardTypeData(item.CardType),
                    cardName: item.NickName,
                    expirationDate,
                };
            }
            return {
                accountType: item.BankAccountHolderType,
                lastFourNumbers: item.BankAccountLastFour,
                type: item.Type,
                id: item.Id,
                key: `${item.BankAccountLastFour}-${item.BankAccountHolderType}`,
                accountName: item.NickName,
                expirationDate: '',
                isDefault: item.IsDefault,
            };
        },

        handlePaymentFinished() {
            this.isPaying = false;
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        handleViewInvoice() {
            this.closeWidget();
            this.$router.push({
                hash: 'Invoices',
                query: { id: this.paymentDetails.invoiceId, submenu: 'mdf' },
            });
        },

        formatDate(date) {
            if (date) {
                return this.$moment.utc(date).format('MMM D, YYYY');
            }
            return '';
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        updateDetails(details) {
            this.paymentDetails = {
                ...this.paymentDetails,
                ...details,
            };
        },

        updateIsLoading(isLoading) {
            this.isLoading = isLoading;
        },
    },
};
</script>

<style>
.mdf-payment-widget {
    color: #382153;
}

.mdf-payment-widget .save-final-btn {
    width: 128px;
    height: 40px;
}

.mdf-payment-widget .cancel-btn {
    background: transparent;
    border: none;
    font: normal normal medium 14px/24px Roboto;
    color: #877d88;
    min-width: 128px;
    min-height: 40px;
}

.mdf-payment-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.mdf-payment-widget .save-btn {
    background: transparent linear-gradient(0deg, #28da34 0%, #13e9e7 100%) 0% 0% no-repeat
        padding-box;
    border-radius: 20px;
    width: 205px;
    height: 40px;
}

.mdf-payment-widget .save-btn:hover {
    background: transparent linear-gradient(0deg, #28da34 0%, #13e9e7 100%) 0% 0% no-repeat
        padding-box;
    border-radius: 20px;
}
</style>
